import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import Breadcrumb from '../components/breadcrumb';
import StepProgressBar from '../components/StepProgressBar';
import JobsiteDetailsStep1 from '../components/JobsiteDetailsStep1';
import AddressStep, { POSTAL_CODE_PROVINCE_MAP } from '../components/AddressStep';
import Dialog from '../components/Dialog';
import { JobsiteService, JobsiteUpdateDTO, JobsiteAddressUpdateDTO } from '../services/jobsite.service';
import { GeneralContractorService } from '../services/general-contractor.service';
import { JobsiteVisitsService } from '../services/jobsite-visits.service';
import { authService } from '../services/auth.service';
import { useJobsiteData } from '../hooks/useJobsiteData';
import { useEditForm } from '../hooks/useEditForm';
import { useJobsiteForm } from '../hooks/useJobsiteForm';
import { JobsiteDetailsSkeleton } from '../components/Skeleton';
import { isVisitEditable } from '../utils/dateUtils';

// Navigation Component
const Navigation = ({ onBack }: { onBack: () => void }) => (
  <div className="mb-6 flex items-center">
    <ArrowLeft
      className="text-gray-600 mr-2 w-5 h-5 cursor-pointer"
      onClick={onBack}
      id="back-button"
    />
    <Breadcrumb title="Edit Jobsite" />
  </div>
);

const EditJobsite: React.FC = () => {
  // Get current user
  const user = authService.getUser();

  const navigate = useNavigate();
  const params = useParams();
  const jobId = params.id;

  // Custom hooks
  const { jobsiteData } = useJobsiteData(jobId, false);
  const {
    completionSteps,
    sectors,
    industries,
    fundingResources,
    provinces,
    trades
  } = useEditForm();

  // Fetch general contractors separately (not from hook)
  const [generalContractors, setGeneralContractors] = useState<any[]>([]);

  // Use jobsite form hook for shared logic
  const {
    isProcessing,
    setIsProcessing,
    processingText,
    setProcessingText,
    handleUpdateJobsiteAddress
  } = useJobsiteForm();

  // State for form steps
  const [currentStep, setCurrentStep] = useState(1);
  const [hasEdits, setHasEdits] = useState(false);

  // Dialog states
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  // State for visit checking
  const [hasVisits, setHasVisits] = useState(false);
  const [checkingVisits, setCheckingVisits] = useState(true);

  // Form data state (matching add-jobsite structure)
  const [formData, setFormData] = useState({
    name: '',
    subsite: '',
    contact: '',
    projectType: '',
    percentageCompleted: '',
    isUnion: false,
    contractor: '',
    address: '',
    city: '',
    country: '',
    state: '',
    postalCode: '',
    superintendent: '',
    siteContactEmail: '',
    sectors: [] as string[],
    industry: '',
    fundingResource: '',
    activityStatus: 'active',
    completionStatus: 'Pre-construction',
    completionStep: 'Pre-construction',
    address1: '',
    address2: '',
    markAsComp: false,
    status: 'active' as 'active' | 'inactive',
    hasPLA: false,
    hasMRF: false,
    projectStartDate: ''
  });

  // Contractor form data for adding new contractors
  const [contractorFormData, setContractorFormData] = useState({
    name: '',
    email: '',
    contractorType: [],
    unionType: 'union',
    phoneNumber: '',
    isSignificant: '',
    multiTrade: false,
    trades: [] as string[],
    singleTrade: '',
    status: 'active',
    tradeUnionTypes: {} as Record<string, string>,
  });

  // Error states
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [contractorFormErrors, setContractorFormErrors] = useState<{
    name?: string;
    email?: string;
    contractorType?: string;
    unionType?: string;
    ownerName?: string;
    phoneNumber?: string;
    isSignificant?: string;
    trades?: string;
    singleTrade?: string;
  }>({});

  // Additional states
  const [showAddContractorDialog, setShowAddContractorDialog] = useState(false);

  // Step configuration
  const FORM_STEPS = [
    { id: 1, title: 'Job Details', description: 'Basic jobsite information and contractor details' },
    { id: 2, title: 'Address', description: 'Jobsite location and address information' }
  ];

  // Load existing jobsite data when it loads
  useEffect(() => {
    if (jobsiteData && jobsiteData.jobDetails[0]) {
      const jobDetails = jobsiteData.jobDetails[0];
      const address = jobsiteData.address[0] || {};

      const loadedFormData = {
        name: jobDetails.jobSiteName || '',
        subsite: jobDetails.subsite || '',
        contact: jobDetails.siteContact || '',
        projectType: '',
        percentageCompleted: '',
        isUnion: false,
        contractor: jobDetails.generalContractor?.id || jobDetails.contractor?.id || '',
        address: address?.address1 || '',
        city: address?.city || '',
        country: 'Canada',
        state: address?.province?.id || '',
        postalCode: address?.postalCode || '',
        superintendent: jobDetails.siteContact || '',
        siteContactEmail: jobDetails.siteContactEmail || '',
        sectors: jobDetails.jobDetailsSectors?.map((jds: any) => jds.sector?.id?.toString()) || [],
        industry: jobDetails.industry?.id || '',
        fundingResource: jobDetails.fundingResource?.id || '',
        activityStatus: (jobDetails.status as 'active' | 'inactive') || 'active',
        completionStatus: jobDetails.jobCompletionStatus?.id || 'start',
        completionStep: jobDetails.jobCompletionStatus?.id || '',
        address1: address?.address1 || '',
        address2: address?.address2 || '',
        markAsComp: jobDetails.markAsComp || false,
        status: (jobDetails.status as 'active' | 'inactive') || 'active',
        hasPLA: jobDetails.hasPLA || false,
        hasMRF: jobDetails.hasMRF || false,
        projectStartDate: jobDetails.projectStartDate || ''
      };

      setFormData(loadedFormData);
      initialFormDataRef.current = loadedFormData;
    }
  }, [jobsiteData]);

  const initialFormDataRef = useRef(formData);

  useEffect(() => {
    const isEdited = JSON.stringify(formData) !== JSON.stringify(initialFormDataRef.current);
    setHasEdits(isEdited);
  }, [formData]);

  // Check if jobsite has any visits
  useEffect(() => {
    const checkForVisits = async () => {
      if (!jobId) return;

      try {
        setCheckingVisits(true);
        const { totalCount } = await JobsiteVisitsService.getJobsiteVisitRecordsByJobId(jobId, {
          page: '1',
          limit: '1',
          sortBy: 'visitDate',
          sortOrder: 'DESC'
        });
        setHasVisits(totalCount > 0);
      } catch (error) {
        console.error('Error checking for visits:', error);
        // On error, assume no visits to allow editing
        setHasVisits(false);
      } finally {
        setCheckingVisits(false);
      }
    };

    checkForVisits();
  }, [jobId]);

  // Fetch general contractors
  useEffect(() => {
    const fetchGeneralContractors = async () => {
      try {
        const contractors = await GeneralContractorService.getGeneralContractors();
        setGeneralContractors(contractors);
      } catch (error) {
        toast.error('Error fetching general contractors');
      }
    };
    fetchGeneralContractors();
  }, []);

  // Track when user is navigating away and block it if there are unsaved changes
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Custom navigation handler that checks for unsaved changes
  const navigateWithWarning = useCallback((to: string) => {
    if (hasEdits) {
      setPendingNavigation(to);
      setShowUnsavedWarning(true);
    } else {
      navigate(to);
    }
  }, [hasEdits, navigate]);

  // Global navigation interception
  useEffect(() => {
    const handleNavigationAttempt = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (!hasEdits) return;

      // Check for links
      const link = target.closest('a[href]') as HTMLAnchorElement;
      if (link) {
        const href = link.getAttribute('href');
        if (href && (href.startsWith('/') || href.startsWith(window.location.origin))) {
          event.preventDefault();
          event.stopPropagation();

          let path = href;
          if (href.startsWith(window.location.origin)) {
            path = href.replace(window.location.origin, '');
          }

          setPendingNavigation(path);
          setShowUnsavedWarning(true);
          return;
        }
      }

      // Check for any clickable element inside the sidebar
      const isInSidebar = target.closest('[data-testid="sidebar"]');
      if (isInSidebar) {
        // Check if this element or its parent has navigation characteristics
        let currentElement = target;
        let navigationPath = null;

        while (currentElement && isInSidebar.contains(currentElement)) {
          // Look for cursor-pointer class which indicates clickable navigation
          if (currentElement.className.includes('cursor-pointer') ||
            currentElement.hasAttribute('role') ||
            currentElement.onclick) {

            // Additional check: make sure it's not a password visibility toggle or similar
            const isPasswordToggle = currentElement.closest('[data-testid="toggle-password-visibility"]');
            const isSubmitButton = (currentElement as HTMLInputElement).type === 'submit' ||
              currentElement.className.includes('btn-primary') ||
              currentElement.className.includes('btn-secondary');

            if (!isPasswordToggle && !isSubmitButton) {
              // Try to determine navigation path from the sidebar item
              const sidebarItem = currentElement.querySelector('[data-testid^="sidebar-item-"]') ||
                currentElement.closest('[data-testid^="sidebar-item-"]');

              if (sidebarItem) {
                const testId = sidebarItem.getAttribute('data-testid') || '';

                // Map the testid to navigation paths
                const pathMap: { [key: string]: string } = {
                  'sidebar-item-dashboard': '/dashboard',
                  'sidebar-item-user-management': '/user-management',
                  'sidebar-item-jobsite-management': '/jobsite-management',
                  'sidebar-item-contractor-management': '/contractor-management',
                  'sidebar-item-general-contractor-management': '/general-contractor-management',
                  'sidebar-item-mechanical-contractor-management': '/mechanical-contractor-management',
                  'sidebar-item-reports': '/reports',
                  'sidebar-item-jobsite-visits': '/jobsite-visits'
                };

                navigationPath = pathMap[testId];
                console.log('Mapped navigation path:', navigationPath);

                // Fallback: try to extract path from text content
                if (!navigationPath) {
                  const text = sidebarItem.textContent?.toLowerCase().replace(/\s+/g, '-');
                  const textToPathMap: { [key: string]: string } = {
                    'dashboard': '/dashboard',
                    'user-management': '/user-management',
                    'jobsite-management': '/jobsite-management',
                    'contractor-management': '/contractor-management',
                    'general-contractor-management': '/general-contractor-management',
                    'mechanical-contractor-management': '/mechanical-contractor-management',
                    'reports': '/reports',
                    'jobsite-visits': '/jobsite-visits'
                  };
                  navigationPath = text ? textToPathMap[text] : null;
                  console.log('Fallback navigation path:', navigationPath, 'from text:', text);
                }
              }

              event.preventDefault();
              event.stopPropagation();
              setPendingNavigation(navigationPath);
              setShowUnsavedWarning(true);
              return;
            }
          }
          currentElement = currentElement.parentElement as HTMLElement;
        }
      }

      // Check for buttons and other elements that might trigger navigation
      const clickable = target.closest('[data-navigate]') as HTMLElement;
      if (clickable) {
        const navigateTo = clickable.getAttribute('data-navigate');
        if (navigateTo) {
          event.preventDefault();
          event.stopPropagation();
          setPendingNavigation(navigateTo);
          setShowUnsavedWarning(true);
          return;
        }
      }
    };

    // Add click listener to document with capture phase
    document.addEventListener('click', handleNavigationAttempt, true);

    return () => {
      document.removeEventListener('click', handleNavigationAttempt, true);
    };
  }, [hasEdits]);

  // Additional navigation interception using popstate event (back/forward browser buttons)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (hasEdits) {
        // Prevent the navigation by pushing current state back
        window.history.pushState(null, '', window.location.href);
        setShowUnsavedWarning(true);
      }
    };

    if (hasEdits) {
      // Add a state to the history so we can intercept back button
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasEdits]);

  // Navigation handlers
  const handleBack = useCallback(() => {
    // Use custom navigation with warning check
    navigateWithWarning(`/jobsite-management/details/${jobId}`);
  }, [navigateWithWarning, jobId]);



  // Input change handlers (matching add-jobsite pattern)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  // Step validation (matching add-jobsite patterns)
  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Job site name is required';
    }
    // Site contact is optional
    // Site contact email is optional, but validate format if provided
    if (formData.siteContactEmail.trim() && !/\S+@\S+\.\S+/.test(formData.siteContactEmail)) {
      newErrors.siteContactEmail = 'Please enter a valid email address';
    }
    if (!formData.sectors || formData.sectors.length === 0) {
      newErrors.sectors = 'At least one sector is required';
    }

    // Check if there are industries available for the selected sectors
    // Only require industry if exactly ONE sector is selected and it has industries
    const availableIndustries = formData.sectors && formData.sectors.length === 1
      ? industries.filter(industry => formData.sectors.includes(industry.sector.id.toString()))
      : [];

    // Only require industry if there are industries available for the single selected sector
    if (availableIndustries.length >= 2 && !formData.industry) {
      newErrors.industry = 'Industry is required';
    }
    if (!formData.projectStartDate) {
      newErrors.projectStartDate = 'Project start date is required';
    }
    if (!formData.completionStep && !formData.markAsComp) {
      newErrors.completionStep = 'Job completion status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.address1.trim()) {
      newErrors.address1 = 'Address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state) {
      newErrors.state = 'Province is required';
    }
    if (formData.postalCode && !/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(formData.postalCode.trim())) {
      newErrors.postalCode = 'Please enter a valid Canadian postal code (e.g., A1A 1A1)';
    } else if (formData.postalCode && formData.state) {
      const prefix = formData.postalCode[0].toUpperCase();
      const validProvinces = POSTAL_CODE_PROVINCE_MAP[prefix];
      const selectedProvince = provinces.find((p: { id: string | number; name: string }) => p.id.toString() === formData.state);
      if (validProvinces && selectedProvince && !validProvinces.includes(selectedProvince.name)) {
        newErrors.postalCode = `This postal code does not belong to ${selectedProvince.name}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!user || !user.id || !jobsiteData) return;

    try {
      setIsProcessing(true);
      setProcessingText('Updating jobsite...');

      const jobDetails = jobsiteData.jobDetails[0];
      const address = jobsiteData.address[0];

      // Determine which fields have changed for duplicate checking
      const nameChanged = formData.name !== (jobDetails.jobSiteName || '');
      const contractorChanged = formData.contractor !== (jobDetails.generalContractor?.id || jobDetails.contractor?.id || '');

      // Calculate address changes for duplicate checking
      const addressDetailsChanged =
        formData.address1 !== (address?.address1 || '') ||
        formData.address2 !== (address?.address2 || '') ||
        formData.city !== (address?.city || '') ||
        formData.state !== (address?.province?.id || '') ||
        formData.postalCode !== (address?.postalCode || '');

      // Check for duplicates if name, contractor, or address changed
      if (nameChanged || contractorChanged || addressDetailsChanged) {
        setProcessingText('Checking for duplicates...');

        const duplicateCheck = await JobsiteService.checkDuplicateJobsite({
          jobSiteName: formData.name,
          generalContractor: formData.contractor,
          address: {
            address1: formData.address1,
            address2: formData.address2,
            city: formData.city,
            province: formData.state,
            postalCode: formData.postalCode || '',
          },
          excludeJobsiteId: jobId // Exclude current jobsite from check
        });

        if (duplicateCheck.isDuplicate) {
          throw new Error('A jobsite with this name, address, and general contractor combination already exists');
        }

        setProcessingText('Updating jobsite...');
      }

      // Check if address fields have changed OR if address doesn't exist yet (needs creation)
      const addressChanged = !address?.id || // Address doesn't exist, needs creation
        formData.address1 !== (address?.address1 || '') ||
        formData.address2 !== (address?.address2 || '') ||
        formData.city !== (address?.city || '') ||
        formData.postalCode !== (address?.postalCode || '') ||
        formData.state !== (address?.province?.id || '');

      // Check if jobsite details have changed
      const existingSectors = jobDetails.jobDetailsSectors?.map((jds: any) => jds.sector?.id?.toString()) || [];
      const sectorsChanged = JSON.stringify(formData.sectors.sort()) !== JSON.stringify(existingSectors.sort());

      const jobDetailsChanged =
        formData.name !== (jobDetails.jobSiteName || '') ||
        formData.subsite !== (jobDetails.subsite || '') ||
        formData.superintendent !== (jobDetails.siteContact) ||
        formData.siteContactEmail !== (jobDetails.siteContactEmail) ||
        sectorsChanged ||
        formData.industry !== (jobDetails.industry?.id || '') ||
        formData.fundingResource !== (jobDetails.fundingResource?.id || '') ||
        formData.activityStatus !== (jobDetails.status || 'active') ||
        formData.completionStep !== (jobDetails.jobCompletionStatus?.id || '') ||
        formData.markAsComp !== (jobDetails.markAsComp || false) ||
        formData.contractor !== (jobDetails.generalContractor?.id || jobDetails.contractor?.id || '') ||
        formData.hasPLA !== (jobDetails.hasPLA || false) ||
        formData.hasMRF !== (jobDetails.hasMRF || false) ||
        formData.projectStartDate !== (jobDetails.projectStartDate || '');
      // Only update job details if they changed
      if (jobDetailsChanged) {
        const jobDetailsData: JobsiteUpdateDTO = {
          jobSiteName: formData.name,
          subsite: formData.subsite,
          siteContact: formData.superintendent,
          siteContactEmail: formData.siteContactEmail,
          sectors: formData.sectors,
          industry: formData.industry,
          fundingResource: formData.fundingResource || undefined,
          status: formData.activityStatus as 'active' | 'inactive',
          jobCompletionStatus: formData.completionStep,
          markAsComp: formData.markAsComp,
          generalContractor: formData.contractor,
          userId: user.id,
          hasPLA: formData.hasPLA,
          hasMRF: formData.hasMRF,
          projectStartDate: formData.projectStartDate
        };
        await JobsiteService.updateJobsiteDetails(jobDetails.id, jobDetailsData);
      }
      // Only update address if it changed
      if (addressChanged) {
        // Use hook to handle address update with geocoding
        await handleUpdateJobsiteAddress(address, formData, user, jobDetails.id);
      }

      // Show appropriate message
      if (jobDetailsChanged || addressChanged) {
        setHasEdits(false);

        // Close confirmation dialog and show success dialog
        setShowConfirmDialog(false);
        setShowSuccessDialog(true);

        // Navigate to jobsite details after a delay
        setTimeout(() => {
          navigate(`/jobsite-management/details/${jobId}`);
        }, 2000);
      } else {
        toast.info('No changes detected');
        setShowConfirmDialog(false);
        navigate(`/jobsite-management/details/${jobId}`);
      }
    } catch (error: any) {
      console.error('Error updating jobsite:', error);
      setShowConfirmDialog(false);
      toast.error(error.message || 'Failed to update jobsite');
    } finally {
      setIsProcessing(false);
      setProcessingText('');
    }
  };

  // Navigation between steps
  const handleNavigate = async (direction: 'next' | 'prev') => {
    if (direction === 'prev') {
      setCurrentStep(prev => prev - 1);
      return;
    }

    // Validate and submit current step
    try {
      switch (currentStep) {
        case 1:
          if (!validateStep1()) {
            toast.error('Please fill in all required fields');
            return;
          }
          break;
        case 2:
          if (!validateStep2()) {
            toast.error('Please fill in all required address fields correctly');
            return;
          }
          // Show confirmation dialog instead of submitting directly
          setShowConfirmDialog(true);
          return;
      }

      setCurrentStep(prev => prev + 1);
    } catch (error: any) {
      toast.error(error.message || 'An error occurred. Please try again.');
    }
  };

  // Add contractor handler
  const handleAddContractor = async (): Promise<boolean> => {
    try {
      const contractorErrors: Record<string, string> = {};

      if (!contractorFormData.name.trim()) {
        contractorErrors.name = 'Contractor name is required';
      }

      if (Object.keys(contractorErrors).length > 0) {
        setContractorFormErrors(contractorErrors);
        return false;
      }

      setIsProcessing(true);
      setProcessingText('Adding contractor...');

      const result = await GeneralContractorService.createGeneralContractor({
        name: contractorFormData.name,
        active: true
      }) as any;

      if (result && result.id) {
        // Reset contractor form
        setContractorFormData({
          name: '',
          email: '',
          contractorType: [],
          unionType: 'union',
          phoneNumber: '',
          isSignificant: '',
          multiTrade: false,
          trades: [],
          singleTrade: '',
          status: 'active',
          tradeUnionTypes: {},
        });
        setContractorFormErrors({});

        // Select the newly created contractor
        setFormData(prev => ({ ...prev, contractor: result.id }));

        toast.success('Contractor added successfully');
        return true;
      }

      return false;
    } catch (error: any) {
      console.error('Error adding contractor:', error);
      toast.error(error.message || 'Failed to add contractor');
      return false;
    } finally {
      setIsProcessing(false);
      setProcessingText('');
    }
  };

  // Render current step content
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <JobsiteDetailsStep1
            formData={formData}
            errors={errors}
            tradeOptions={trades}
            sectors={sectors}
            industries={industries}
            fundingResources={fundingResources}
            contractors={generalContractors}
            trades={trades}
            selectedContractor={formData.contractor}
            completionSteps={completionSteps}
            disableCompletionStatus={hasVisits}
            completionStatusMessage={hasVisits ? "Completion status is managed by jobsite visits" : undefined}
            onInputChange={handleInputChange}
            onContractorSelect={(value) => {
              setFormData(prev => ({ ...prev, contractor: value }));
              if (errors.contractor) {
                setErrors(prev => ({ ...prev, contractor: '' }));
              }
            }}
            onAddContractor={async () => {
              const success = await handleAddContractor();
              if (success) {
                setShowAddContractorDialog(false);
              }
              return success;
            }}
            onCompletionStepChange={(step) => setFormData(prev => ({
              ...prev,
              completionStep: step,
              completionStatus: step
            }))}
            onActivityStatusChange={(isActive) => setFormData(prev => ({
              ...prev,
              activityStatus: isActive ? 'active' : 'inactive'
            }))}
            onSectorsChange={(values) => {
              setFormData(prev => {
                // Clear industry when sectors change (industries are sector-specific)
                const sectorsChanged = JSON.stringify(values.sort()) !== JSON.stringify(prev.sectors.sort());
                const newIndustry = (values.length > 1 || sectorsChanged) ? '' : prev.industry;
                return {
                  ...prev,
                  sectors: values,
                  industry: newIndustry
                };
              });
              if (errors.sectors) {
                setErrors(prev => ({ ...prev, sectors: '' }));
              }
            }}
            onIndustryChange={(value) => {
              setFormData(prev => ({ ...prev, industry: value }));
              if (errors.industry) {
                setErrors(prev => ({ ...prev, industry: '' }));
              }
            }}
            onFundingResourceChange={(value) => {
              setFormData(prev => ({ ...prev, fundingResource: value }));
              if (errors.fundingResource) {
                setErrors(prev => ({ ...prev, fundingResource: '' }));
              }
            }}
            onMarkAsCompChange={(checked) => {
              setFormData(prev => ({ ...prev, markAsComp: checked }));
            }}
            onProjectStartDateChange={(value) => {
              setFormData(prev => ({ ...prev, projectStartDate: value }));
              if (errors.projectStartDate) {
                setErrors(prev => ({ ...prev, projectStartDate: '' }));
              }
            }}
            provinces={provinces}
            onContractorAdded={async (contractorId: string) => {
              // Refresh general contractor list after new contractor is created
              try {
                const updatedContractors = await GeneralContractorService.getGeneralContractors();
                setGeneralContractors(updatedContractors);
                setFormData(prev => ({ ...prev, contractor: contractorId }));
              } catch (error) {
                console.error('Failed to refresh general contractor list:', error);
              }
            }}
          />
        );
      case 2:
        return (
          <AddressStep
            formData={{
              address1: formData.address1,
              address2: formData.address2,
              city: formData.city,
              state: formData.state,
              postalCode: formData.postalCode,
              country: formData.country,
            }}
            errors={errors}
            setFormData={(addressData: any) => {
              setFormData(prev => ({
                ...prev,
                address1: addressData.address1,
                address2: addressData.address2,
                city: addressData.city,
                postalCode: addressData.postalCode,
                state: addressData.state,
                country: addressData.country
              }));
            }}
            setErrors={(addressErrors) => {
              setErrors(prev => ({
                ...prev,
                ...addressErrors
              }));
            }}
            provinces={provinces}
            handleInputChange={handleInputChange}
          />
        );
      default:
        return null;
    }
  };

  // Handle unsaved changes warning dialog
  const handleConfirmNavigation = () => {
    console.log('Confirming navigation to:', pendingNavigation);
    setShowUnsavedWarning(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    } else {
      console.log('No pending navigation path found');
    }
  };

  const handleCancelNavigation = () => {
    setShowUnsavedWarning(false);
    setPendingNavigation(null);
  };

  // Show loading skeleton while jobsite data is loading
  if (!jobsiteData) {
    return (
      <div className="bg-gray-50 p-3 sm:p-6">
        <Navigation onBack={handleBack} />
        <JobsiteDetailsSkeleton />
      </div>
    );
  }

  // Guard: block editing outside the 60-day window
  if (!isVisitEditable(jobsiteData.jobDetails[0]?.createdAt)) {
    toast.error('Editing is only available within 60 days of jobsite creation.');
    navigate(`/jobsite-management/details/${jobId}`);
    return null;
  }

  return (
    <div className="bg-gray-50 p-3 sm:p-6">
      <Navigation onBack={handleBack} />

      {/* Main Content */}
      <div className="bg-white rounded-md shadow">
        <div className="px-4 py-5 sm:px-7 sm:py-7 md:px-10 md:py-8 lg:px-14 lg:py-10 xl:px-18">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-semibold">Edit Jobsite</h1>
            <p className="text-gray-600 mt-2">{jobsiteData.jobDetails[0]?.jobSiteName}</p>
          </div>

          {/* Step Progress Bar */}
          <div className="mb-8">
            <StepProgressBar
              steps={FORM_STEPS.map(step => step.title)}
              currentStep={currentStep}
            />
          </div>

          {/* Form Content */}
          <div className="mb-8">
            {renderCurrentStep()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => handleNavigate('prev')}
              disabled={currentStep === 1 || isProcessing}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <button
              type="button"
              onClick={() => handleNavigate('next')}
              disabled={isProcessing}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? processingText : (currentStep === FORM_STEPS.length ? 'Update Jobsite' : 'Next')}
            </button>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Warning Dialog */}
      {showUnsavedWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Unsaved Changes</h3>
            <p className="text-gray-600 mb-6">
              You have unsaved changes. Are you sure you want to leave this page?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={handleCancelNavigation}
                className="px-4 py-2 btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmNavigation}
                className="px-4 py-2 btn-primary"
              >
                Leave Page
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        isOpen={showConfirmDialog}
        onClose={() => !isProcessing && setShowConfirmDialog(false)}
        title={isProcessing ? "Processing" : "Confirm Jobsite Update"}
      >
        {isProcessing ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#A42823] mb-4"></div>
            <p className="text-gray-600">{processingText || 'Processing...'}</p>
          </div>
        ) : (
          <div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to update this jobsite? Please review all the changes before proceeding.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="btn-secondary"
                id="cancel-update-button"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="btn-primary flex items-center gap-2"
                id="confirm-update-button"
              >
                Update Jobsite
              </button>
            </div>
          </div>
        )}
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        isOpen={showSuccessDialog}
        onClose={() => {}}
        title="Success"
      >
        <div className="text-center py-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-lg font-medium text-gray-900 mb-2">
            Jobsite Updated Successfully!
          </p>
          <p className="text-sm text-gray-600">
            Redirecting to jobsite details...
          </p>
        </div>
      </Dialog>

    </div>
  );
};

export default EditJobsite;